// Consolidated utilities for Hotspot components

/**
 * Tooltip positioning and data utilities
 */
export const initTooltipDirection = (data, viewData) => {
  const viewLimits = {
    leftLimit: viewData.x_offset,
    rightLimit: viewData.x_offset + viewData.size.width,
    topLimit: viewData.y_offset,
    bottomLimit: viewData.y_offset + viewData.size.height,
  };
  const hotspotLimits = {
    leftLimit: data.x_offset,
    rightLimit: data.x_offset + data.width,
    topLimit: data.y_offset,
    bottomLimit: data.y_offset + data.height,
  };

  const topDiff = hotspotLimits.topLimit - viewLimits.topLimit;
  const bottomDiff = viewLimits.bottomLimit - hotspotLimits.bottomLimit;
  const vert = topDiff > bottomDiff ? "top" : "bottom";
  const leftDiff = hotspotLimits.leftLimit - viewLimits.leftLimit;
  const rightDiff = viewLimits.rightLimit - hotspotLimits.rightLimit;
  const horr = leftDiff > rightDiff ? "left" : "right";

  return `${vert}-${horr}`;
};

export const getTooltipData = (data) => {
  const isDropdownType =
    data?.props?.type === "state-changer" &&
    data?.props?.stateChangerType === "Dropdown";
  const opts = data?.props?.options ?? [];
  const currState1 = data?.props?.currentState ?? 1; // 1-based
  const currIndex0 = Math.max(0, currState1 - 1);
  const currOpt = isDropdownType ? opts[currIndex0] : undefined;
  const isCorrectVariant = !!currOpt && currOpt.correctness === "Correct";
  const isIncorrectVariant = !!currOpt && currOpt.correctness === "Incorrect";
  const derivedTooltipText =
    isIncorrectVariant || isCorrectVariant
      ? (currOpt?.name ?? "")
      : (data?.tooltipText ?? "");

  return {
    isCorrectVariant,
    isIncorrectVariant,
    derivedTooltipText,
    currOpt,
  };
};

/**
 * Text body size calculation utilities
 */
export const checkTextBodySize = (
  id,
  data,
  isDiabledBlurOnSmall,
  setTextBodyWidth,
  setTextBodyHeight,
  setTextBodyIsSmall
) => {
  const elem = document.getElementById(`textBody${id}`);
  if (elem !== null) {
    const width = elem.offsetWidth;
    const height = elem.offsetHeight;
    setTextBodyWidth(width);
    setTextBodyHeight(height);

    const minPixPerCell = 25;
    const addPixPerCell = 50;
    const pixGrowthPerCell = 1.5;
    const maxThreshhold = 300;

    const threshhold =
      data.width * minPixPerCell +
      addPixPerCell * ((data.width - 1) * pixGrowthPerCell);

    if (
      ((width < threshhold && width < maxThreshhold) || height < 55) &&
      !isDiabledBlurOnSmall
    ) {
      setTextBodyIsSmall(true);
    } else {
      setTextBodyIsSmall(false);
    }
  }
};

/**
 * Click handler creation utility
 */
export const createClickHandler = (props) => {
  const getCoordsData = () => {
    const elem = document.getElementById(`hotspotBtn_${props.id}`);
    const coords = elem.getBoundingClientRect();
    return coords;
  };

  return () => {
    switch (props.data.props.type) {
      case "view-changer":
        props.changeView(props.data.props.setView);
        if (!props.data.triggered && props.data.postTrigger !== "no-change") {
          props.handleTrigger(props.id);
        }
        break;
      case "state-changer":
        if (
          !props.data.props.stateChangerType ||
          props.data.props.stateChangerType === "Cycle (default)"
        ) {
          props.changeState(props.id);
          if (!props.data.triggered && props.data.postTrigger !== "no-change") {
            props.handleTrigger(props.id);
          }
        }
        break;
      case "content-box":
        if (!props.data.triggered && props.data.postTrigger !== "no-change") {
          props.handleTrigger(props.id);
        }
        break;
      case "text-body":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        if (!props.data.triggered && props.data.postTrigger !== "no-change") {
          props.handleTrigger(props.id);
        }
        break;
      case "CSCQ":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "TABLE_QUESTION":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "MSCQ":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "learn-more":
        props.updateWidgetData(
          true,
          props.data.props.type,
          props.id,
          getCoordsData()
        );
        break;
      case "none":
        props.handleTrigger(props.id);
        if (props.data.showUsersnapFeedback) {
          props.loadUsersnap().then((usersnapApi) => {
            if (usersnapApi) {
              usersnapApi
                .show(props.usersnapProjectId)
                .then((widgetApi) => widgetApi.open());
            } else {
              console.warn("Usersnap API not available");
            }
          });
        }
        break;
      default:
        null;
    }
  };
};

/**
 * Hotspot type detection utilities
 */
export const isDropdownHotspot = (data) => {
  return (
    data.props.type === "state-changer" &&
    data.props.stateChangerType === "Dropdown"
  );
};

export const isContentBoxHotspot = (data) => {
  return data.props.type === "content-box";
};

export const isTextBodyHotspot = (data) => {
  return data.props.type === "text-body";
};

/**
 * Common hotspot style calculations
 */
export const getHotspotStyles = (data, cellSize) => {
  if (data.props.type === "content-box") {
    return {};
  }

  return {
    maxHeight: `clamp(24px, ${(38 / 100) * cellSize}px, ${cellSize}px)`,
    minHeight: `clamp(24px, 40px, ${cellSize}px)`,
    maxWidth: `clamp(24px, ${(38 / 100) * cellSize}px, ${cellSize}px)`,
    minWidth: `clamp(24px, 40px, ${cellSize}px)`,
    borderWidth: `clamp(1px, ${0.025 * cellSize}px, ${0.025 * cellSize}px)`,
  };
};

/**
 * Common hotspot class name generation
 */
export const getHotspotClassNames = (props, className) => {
  const { data, id, selectedHotspot, locked, hide, showGrid, idle } = props;

  return [
    className.hotspotContainer,
    data.props.type === "content-box"
      ? className.hotspotContainerContentBox
      : "",
    props.isCorrect === false
      ? className.hotspotContainerContentBoxIncorrect
      : "",
    props.isCorrect === true ? className.hotspotContainerContentBoxCorrect : "",
    id === selectedHotspot ? className.selected : "",
    data.triggered ? getPostTriggerClassName(data.postTrigger, className) : "",
    locked && !data.triggered ? className.locked : "",
    hide ? className.showHover : "",
    !showGrid ? className.showHoverOverride : "",
    hide && idle ? className.idle : "",
    ["Next", "Back", "Down", "Up"].includes(data.visualOverride)
      ? className.goldNav
      : "",
    data.visualOverride === "Orange" ? className.orange : "",
  ]
    .filter(Boolean)
    .join(" ");
};

const getPostTriggerClassName = (postTrigger, className) => {
  const triggerMap = {
    "no-change": className.noChange,
    inactive: className.inactive,
    triggered: className.triggered,
    hidden: className.hidden,
  };
  return triggerMap[postTrigger] || "";
};
